import { useCallback, useState } from 'react';

import { DatarouterError } from '@zg-rentals/web-base';

import ListingActivationConstants from '../constants/ListingActivationConstants';

import DigitallyAlteredPhotosAttestationModal from '../components/modals/DigitallyAlteredPhotosAttestationModal';

import { useActivateListing } from './queries/useActivateListing';

type ActivateArgs = Parameters<ReturnType<typeof useActivateListing>['mutate']>;

const isAttestationRequiredError = (error: unknown) =>
  DatarouterError.is(error) &&
  error.code === ListingActivationConstants.ERROR_CODE.DIGITALLY_ALTERED_PHOTOS_ATTESTATION_REQUIRED;

export const useActivateListingWithAttestation = () => {
  const { mutate, isPending } = useActivateListing();
  const [blockedActivation, setBlockedActivation] = useState<ActivateArgs>();

  const activate = useCallback(
    (...args: ActivateArgs) => {
      const [variables, options] = args;

      mutate(variables, {
        ...options,
        onError: (error, ...rest) => {
          if (isAttestationRequiredError(error)) {
            setBlockedActivation(args);
            return;
          }
          options?.onError?.(error, ...rest);
        },
      });
    },
    [mutate],
  );

  const handleAttestationContinue = () => {
    if (!blockedActivation) return;
    setBlockedActivation(undefined);
    activate(...blockedActivation);
  };

  const attestationModal = (
    <DigitallyAlteredPhotosAttestationModal
      listingAlias={blockedActivation?.[0].postData.listingAlias ?? ''}
      isOpen={blockedActivation !== undefined}
      onClose={() => setBlockedActivation(undefined)}
      onContinue={handleAttestationContinue}
      submitText="Continue"
    />
  );

  return { activate, isPending, attestationModal };
};
